/**
 * PatientPortalShell — top-level layout for the Therapist-Led patient portal.
 *
 * Renders when the logged-in user's serviceGroup.clientUIMode === 'patient_portal'.
 * Replaces the standard client app shell entirely (not a conditional inside it),
 * so an AI-Forward user CAN'T accidentally end up in here.
 *
 * Nav:
 *   - Today (default home)
 *   - Tasks (pre-session)
 *   - Summaries (therapist-shared)
 *   - Documents (secure share)
 *   - Crisis (always one-click reachable from header)
 *   - Settings
 *
 * Spec: docs/working-notes/therapist-led-implementation-plan-v2.md Spec C
 */

import React from 'react';
import NotificationBell from '../../components/common/NotificationBell';
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Typography, Button, IconButton, Container,
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider,
  useMediaQuery, useTheme
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import MissedCallBanner from '../../components/call/MissedCallBanner';
import SupportTicketDialog from '../../components/chat/SupportTicketDialog';

const NAV = [
  { path: '/portal', label: 'Today', icon: <HomeIcon /> },
  { path: '/portal/tasks', label: 'Tasks', icon: <AssignmentIcon /> },
  { path: '/portal/summaries', label: 'Summaries', icon: <DescriptionIcon /> },
  { path: '/portal/documents', label: 'Documents', icon: <FolderSharedIcon /> },
  { path: '/portal/settings', label: 'Settings', icon: <SettingsIcon /> }
];

const DRAWER_WIDTH = 240;

/**
 * The portal's top bar: name, Report an issue, Crisis support. Also shown on
 * the call page for portal clients, so the screen after a call looks like the
 * portal rather than the blue app bar (ST-20260925-0004).
 */
export function PortalTopBar({ user, isMobile: isMobileProp, onOpenMenu = null }) {
  const theme = useTheme();
  const isMobileQuery = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = isMobileProp ?? isMobileQuery;
  // ST-20260917-0004: the portal is the only client surface that owns its own
  // header, so it was the only one without a way to report a problem. That left
  // clients waiting for approval unable to tell us anything, which is precisely
  // the window where we can see least.
  const [supportTicketOpen, setSupportTicketOpen] = React.useState(false);
  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: 'background.paper', color: 'text.primary' }}
    >
      <Toolbar>
        {isMobile && onOpenMenu && (
          <IconButton edge="start" onClick={onOpenMenu} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
        )}
        <Typography variant="h6" component={RouterLink} to="/portal" sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none' }}>
          AIMHI · {user?.name?.split(' ')[0] || 'Welcome'}
        </Typography>
        <NotificationBell sx={{ mr: 0.5 }} />
        {/* Secondary to Crisis support, which stays the prominent CTA. An
            icon on mobile so it cannot crowd the crisis button out. */}
        {isMobile ? (
          <IconButton
            onClick={() => setSupportTicketOpen(true)}
            aria-label="Report an issue"
            sx={{ mr: 1 }}
          >
            <FeedbackOutlinedIcon />
          </IconButton>
        ) : (
          <Button
            variant="text"
            color="inherit"
            startIcon={<FeedbackOutlinedIcon />}
            onClick={() => setSupportTicketOpen(true)}
            sx={{ mr: 1 }}
          >
            Report an issue
          </Button>
        )}
        <Button
          variant="contained"
          color="error"
          startIcon={<SupportAgentIcon />}
          component={RouterLink}
          to="/portal/crisis"
          sx={{ fontWeight: 600 }}
        >
          Crisis support
        </Button>
      </Toolbar>

      <SupportTicketDialog
        open={supportTicketOpen}
        onClose={() => setSupportTicketOpen(false)}
        token={localStorage.getItem('token')}
        currentPage="portal"
        reportingFrom="Portal"
      />
    </AppBar>
  );
}

function PatientPortalShell({ user, onLogout, onRefreshUser }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const drawer = (
    <Box sx={{ overflow: 'auto', mt: { md: 8 } }}>
      <List>
        {NAV.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={active}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={onLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Sign out" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Top bar — always shows the Crisis button as a persistent CTA. */}
      <PortalTopBar user={user} isMobile={isMobile} onOpenMenu={() => setMobileOpen(true)} />

      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' }
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' }
          }}
        >
          <Toolbar />
          {drawer}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: 8,
          p: { xs: 2, md: 4 },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }
        }}
      >
        <Container maxWidth="md" sx={{ p: 0 }}>
          {/* Inside the content area so the fixed header cannot cover it (AD-50). */}
          {user && (
            <MissedCallBanner currentUser={user} token={localStorage.getItem('token')} sx={{ mb: 2 }} />
          )}
          <Outlet context={{ user, refreshUser: onRefreshUser }} />
        </Container>
      </Box>
    </Box>
  );
}

export default PatientPortalShell;
